import {
  Button,
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import { EDVCPhoneInput } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import { isValidMobile } from 'src/clinic/views/onboardng/clinicBrand/components/utils'
import { postSPDataWithParams } from 'src/supportPortal/services/services'
import Send from '@mui/icons-material/Send'
import Phone from '@mui/icons-material/Phone'
import Person from '@mui/icons-material/Person'
import { toast } from 'react-toastify'
import { styled } from '@mui/material/styles'

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  padding: '10px 16px',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(67, 56, 202, 0.2)',
  },
}))

const CustomPhoneContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '14px 16px',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
  borderRadius: '10px',
  border: '1.5px solid rgba(67, 56, 202, 0.15)',
  boxShadow: '0 2px 8px rgba(67, 56, 202, 0.08)',
  transition: 'all 0.2s ease',
  '&:focus-within': {
    borderColor: 'rgba(67, 56, 202, 0.4)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.15)',
    background: '#fff',
  },
}))

export default function SendJoiningLinkButtons({
  link,
  bookedEventInfo,
  linkSendingApiSignature,
  sendLinkToHumanSupportNumberApiSignature,
  showSendingLinkButtonsToSupport = false,
}) {
  const [customMobile, setCustomMobile] = useState('')
  const [sendingLinkMessage, setSendingLinkMessage] = useState(false)
  const [sendingToSupport, setSendingToSupport] = useState(false)

  async function sendJoiningLinkToPatient(sendUsingCustomMobile) {
    setSendingLinkMessage(true)
    if (!linkSendingApiSignature) {
      setSendingLinkMessage(false)
      return
    }
    try {
      const response = await postSPDataWithParams(linkSendingApiSignature, null, {
        'booked-event-id': bookedEventInfo?.id,
        'to-custom-mobile': sendUsingCustomMobile
          ? customMobile.startsWith('+')
            ? customMobile
            : `+${customMobile}`
          : null,
      })
      if (response?.status === 200) {
        toast.success(
          sendUsingCustomMobile
            ? 'Link sent to custom number successfully'
            : 'Link sent to patient successfully',
        )
        if (sendUsingCustomMobile) {
          setCustomMobile('')
        }
      } else {
        toast.error('Failed to send link. Please try again.')
      }
    } catch (error) {
      toast.error('Failed to send link. Please try again.')
    } finally {
      setSendingLinkMessage(false)
    }
  }

  async function sendLinkToHumanSupportNumber() {
    setSendingToSupport(true)
    if (!sendLinkToHumanSupportNumberApiSignature) {
      setSendingToSupport(false)
      return
    }
    try {
      const response = await postSPDataWithParams(sendLinkToHumanSupportNumberApiSignature, null, {
        'booked-event-id': bookedEventInfo?.id,
      })
      if (response?.status === 200) {
        toast.success('Link sent to support number successfully')
      } else {
        toast.error('Failed to send link. Please try again.')
      }
    } catch (error) {
      toast.error('Failed to send link. Please try again.')
    } finally {
      setSendingToSupport(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Send to Patient's Mobile */}
      <ActionButton
        onClick={() => {
          sendJoiningLinkToPatient()
        }}
        disabled={sendingLinkMessage}
        variant="contained"
        fullWidth
        size="small"
        startIcon={sendingLinkMessage ? <CircularProgress size={14} color="inherit" /> : <Person sx={{ fontSize: 16 }} />}
        sx={{
          color: '#fff',
          background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
          },
          '&:disabled': {
            background: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.26)',
          },
          fontSize: '12px',
          padding: '8px 12px',
        }}
      >
        To Patient
      </ActionButton>

      {/* Send to Human Support Team */}
      {sendLinkToHumanSupportNumberApiSignature && (
        <ActionButton
          onClick={() => {
            sendLinkToHumanSupportNumber()
          }}
          variant="outlined"
          disabled={sendingToSupport}
          fullWidth
          size="small"
          startIcon={sendingToSupport ? <CircularProgress size={14} /> : <Phone sx={{ fontSize: 16 }} />}
          sx={{
            borderColor: 'rgba(67, 56, 202, 0.3)',
            color: '#4338ca',
            fontSize: '12px',
            padding: '8px 12px',
            '&:hover': {
              borderColor: '#4338ca',
              background: 'rgba(67, 56, 202, 0.04)',
            },
          }}
        >
          To Support Team
        </ActionButton>
      )}

      {/* Custom Mobile Number */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(0, 0, 0, 0.6)',
            fontWeight: 500,
            display: 'block',
            marginBottom: '8px',
            fontSize: '11px',
          }}
        >
          Or enter custom number
        </Typography>
        <CustomPhoneContainer>
          <Box sx={{ flex: 1, minWidth: 0, '& .form-control': { border: 'none', boxShadow: 'none', padding: '0' } }}>
            <EDVCPhoneInput
              specialLabel=""
              placeholder="Enter mobile number"
              country="in"
              preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
              value={customMobile}
              onChange={(value) => {
                setCustomMobile(value || '')
              }}
              inputStyle={{
                border: 'none',
                boxShadow: 'none',
                width: '100%',
                fontSize: '14px',
                padding: '0',
                background: 'transparent',
                outline: 'none',
              }}
              buttonStyle={{
                border: 'none',
                background: 'transparent',
                padding: '0 8px 0 0',
              }}
              dropdownStyle={{
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
          <Tooltip title={isValidMobile(customMobile) ? 'Send link to this number' : 'Enter a valid mobile number'}>
            <span>
              <IconButton
                onClick={() => {
                  sendJoiningLinkToPatient(true)
                }}
                disabled={!isValidMobile(customMobile) || sendingLinkMessage}
                size="small"
                sx={{
                  background: isValidMobile(customMobile)
                    ? 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)'
                    : 'rgba(0, 0, 0, 0.08)',
                  color: isValidMobile(customMobile) ? '#fff' : 'rgba(0, 0, 0, 0.26)',
                  minWidth: '40px',
                  width: '40px',
                  height: '40px',
                  '&:hover': {
                    background: isValidMobile(customMobile)
                      ? 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)'
                      : 'rgba(0, 0, 0, 0.12)',
                  },
                  '&:disabled': {
                    background: 'rgba(0, 0, 0, 0.08)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {sendingLinkMessage ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Send sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </CustomPhoneContainer>
        {customMobile && !isValidMobile(customMobile) && (
          <Typography variant="caption" sx={{ color: '#dc2626', marginTop: '6px', display: 'block', fontSize: '10px' }}>
            Please enter a valid mobile number
          </Typography>
        )}
      </Box>
    </Box>
  )
}
